import { useEffect, useRef } from "react";
import { brand } from "../data/siteData";
import "./AboutUs.css";

const stats = [
  { num: "10+", label: "Years of R&D" },
  { num: "60+", label: "Crops Protected" },
  { num: "12+", label: "States Served" },
  { num: "120+", label: "Products Registered" },
];

const values = [
  {
    icon: "🔬",
    title: "Science First",
    desc: "Every formulation is backed by years of research, field trials, and rigorous safety testing before it reaches a farmer's hands.",
  },
  {
    icon: "🌍",
    title: "Farmer Focused",
    desc: "We design solutions that are practical, affordable, and effective — built for real field conditions across diverse geographies.",
  },
  {
    icon: "♻️",
    title: "Eco Responsible",
    desc: "Sustainability is embedded in everything we do — from low-residue formulations to biodegradable packaging and carbon-neutral operations.",
  },
  {
    icon: "🤝",
    title: "Trust & Integrity",
    desc: "We hold ourselves to the highest regulatory standards globally, ensuring every product is safe for farmers, consumers, and the environment.",
  },
];

const milestones = [
  // { year: "1989", event: "Founded in Sikandrabad, UP — first batch of agrochemicals rolled out" },
  // { year: "1998", event: "Expanded to 12 Indian states with 50+ registered products" },
  // { year: "2005", event: "Launched dedicated R&D centre for biological formulations" },
  // { year: "2012", event: "First international export to South-East Asian markets" },
  // { year: "2018", event: "ISO 9001 & GMP certified manufacturing facility" },
  // { year: "2024", event: "Crossed 140 countries with 500+ registered formulations" },

  { year: "2017", event: "Founded in Gwalior, MP — first batch of agrochemicals rolled out" },
  { year: "2021", event: "Expanded to 5 Indian states with 50+ registered products" },
  { year: "2022", event: "Launched dedicated R&D centre for biological formulations" },
  // { year: "2012", event: "First international export to South-East Asian markets" },
  { year: "2023", event: "ISO 9001 & GMP certified manufacturing facility" },
  { year: "2024", event: "Crossed 12 States with 120+ registered formulations" },
];

const team = [
  // { name: "Parmesh Bajaj", role: "Sales & Marketing", emoji: "👨‍💼", exp: "35 yrs" },
  { name: "Parmesh Bajaj", role: "Sales & Marketing", emoji: "👨‍💼", exp: "35 yrs" },
  { name: "Kaushlesh Sharma", role: "Sales & Marketing", emoji: "👨‍💼", exp: "18 yrs" },
  { name: "Chandrakant Sharma", role: "Sales & Marketing", emoji: "👨‍💼", exp: "18 yrs" },
  // { name: "Sunita Rao", role: "Chief Agronomist", emoji: "👩‍🌾", exp: "15 yrs" },
];

export default function AboutUs() {
  const ref = useRef(null);

  useEffect(() => {
    const obs = new IntersectionObserver(
      (entries) =>
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add("visible");
            obs.unobserve(e.target);
          }
        }),
      { threshold: 0.1 }
    );
    ref.current?.querySelectorAll(".reveal").forEach((el) => obs.observe(el));
  }, []);

  return (
    <section id="about" className="about" ref={ref}>

      {/* ── Hero banner ── */}
      <div className="about__hero">
        <div className="about__hero-bg" />
        <div className="about__hero-content reveal">
          <span className="section-eyebrow about__eyebrow">Who We Are</span>
          <h2 className="about__hero-title">
            Growing with Farmers<br />
            <em>Since 2017</em>
          </h2>
          <p className="about__hero-desc">
            {brand.name} is a leading Indian agri-chemicals company headquartered in Sikandrabad, UP.
            For over a decade, we have been engineering science-backed crop protection solutions
            that empower farmers to grow more, waste less, and feed the world sustainably.
          </p>
          <div className="about__hero-badges">
            <span>🏭 GMP Certified</span>
            <span>🌿 ISO 9001</span>
            <span>✅ CIB&RC Registered</span>
            <span>🌍 Export Certified</span>
          </div>
        </div>

        {/* Stats strip inside hero */}
        <div className="about__stats reveal">
          {stats.map((s) => (
            <div className="about__stat" key={s.label}>
              <div className="about__stat-num">{s.num}</div>
              <div className="about__stat-label">{s.label}</div>
            </div>
          ))}
        </div>
      </div>

      {/* ── Mission & Vision ── */}
      <div className="section about__mv-wrap">
        <div className="about__mv reveal">
          <div className="about__mv-card about__mv-card--mission">
            <div className="about__mv-icon">🎯</div>
            <h3>Our Mission</h3>
            <p>
              To develop and deliver innovative, safe, and sustainable crop protection solutions
              that maximize farm productivity while safeguarding human health and the environment.
            </p>
          </div>
          <div className="about__mv-card about__mv-card--vision">
            <div className="about__mv-icon">🔭</div>
            <h3>Our Vision</h3>
            <p>
              To be the most trusted agri-chemical partner for farmers across the globe —
              connecting cutting-edge science with ground-level agricultural challenges.
            </p>
          </div>
        </div>
      </div>

      {/* ── Core Values ── */}
      <div className="section about__values-wrap">
        <div className="reveal">
          <span className="section-eyebrow">What Drives Us</span>
          <h2 className="section-title">Our Core <em>Values</em></h2>
        </div>
        <div className="about__values reveal">
          {values.map((v) => (
            <div className="about__value-card" key={v.title}>
              <div className="about__value-icon">{v.icon}</div>
              <h4 className="about__value-title">{v.title}</h4>
              <p className="about__value-desc">{v.desc}</p>
            </div>
          ))}
        </div>
      </div>

      {/* ── Journey / Timeline ── */}
      <div className="section about__timeline-wrap">
        <div className="leaf-pattern" />
        <div className="about__timeline-inner">
          <div className="reveal">
            <span className="section-eyebrow about__eyebrow">Our Journey</span>
            <h2 className="section-title about__tl-title">10 Years of <em>Growth</em></h2>
          </div>
          <div className="about__timeline reveal">
            {milestones.map((m, i) => (
              <div className="about__tl-item" key={m.year} style={{ '--i': i }}>
                <div className="about__tl-year">{m.year}</div>
                <div className="about__tl-dot" />
                <div className="about__tl-event">{m.event}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── Leadership Team ── */}
      <div className="section about__team-wrap">
        <div className="reveal">
          <span className="section-eyebrow">The People</span>
          <h2 className="section-title">Leadership <em>Team</em></h2>
          <p className="about__team-sub">
            Decades of combined expertise in agronomy, chemistry, and agricultural business driving our mission forward.
          </p>
        </div>
        <div className="about__team reveal">
          {team.map((t) => (
            <div className="about__team-card" key={t.name}>
              <div className="about__team-avatar">{t.emoji}</div>
              <div className="about__team-exp">{t.exp} exp</div>
              <h4 className="about__team-name">{t.name}</h4>
              <p className="about__team-role">{t.role}</p>
            </div>
          ))}
        </div>
      </div>

      {/* ── CTA Strip ── */}
      <div className="about__cta reveal">
        <div className="about__cta-inner">
          <h3 className="about__cta-title">Ready to Protect Your Crop?</h3>
          <p className="about__cta-desc">Talk to our agronomy team for a tailored crop protection program.</p>
          <div className="about__cta-btns">
            <a href="#contact" className="btn-primary">Get in Touch</a>
            <a href="#products" className="btn-outline">View Products</a>
          </div>
        </div>
      </div>

    </section>
  );
}